/**
 * Module dependencies.
 */

import React from 'react';
import { Controller, useWatch } from 'react-hook-form';
import { InputNumber as AntdInputNumber, Switch } from 'antd';
import { FormField } from 'app/components/atoms/forms/form-field/form-field';
import { ErrorField } from 'app/components/atoms/forms/error-field/error-field';
import { useCrudForm } from 'app/components/organisms/crud/crud-form-provider';
import { TFunction } from 'react-i18next';

/**
 * Export `makeNullableNumberField`.
 *
 * Creates a form component that renders a number input with an "Unlimited" toggle.
 * When the toggle is on, the input is disabled and the field value is `null`.
 * When the toggle is off, the input is enabled and requires a numeric value.
 */

export function makeNullableNumberField(name: string, label: string, translate: TFunction) {
  return function NullableNumberField() {
    const form = useCrudForm();
    const { control } = form;
    const value = useWatch({ control, name });
    const isUnlimited = value === null;

    return (
      <Controller
        control={control}
        name={name}
        render={({ field, fieldState: { error, invalid, isTouched } }) => (
          <FormField label={label}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <AntdInputNumber
                disabled={isUnlimited}
                onChange={(v: number | null) => field.onChange(v === null ? undefined : v)}
                style={{ flex: 1 }}
                value={isUnlimited ? undefined : (field.value as number | undefined)}
              />

              <Switch
                checked={isUnlimited}
                checkedChildren={String(translate('common.labels.unlimited'))}
                onChange={checked => field.onChange(checked ? null : 0)}
                unCheckedChildren={String(translate('common.labels.unlimited'))}
              />
            </div>

            {error?.message && (
              <ErrorField error={{ type: error.type, message: error.message }} isVisible={invalid || isTouched} />
            )}
          </FormField>
        )}
        rules={{
          validate: v => v !== undefined || String(translate('common.errors.form.required'))
        }}
      />
    );
  };
}
